import React, { useCallback, useEffect, useState } from 'react';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { analyticsService, cidCvnService, techlineService } from '@web/services/singletons';
import { CidCvnCheckResultBadge } from '../components/cid-cvn-check-result-badge';
import { CidCvnStartCaseModal } from '../components/cid-cvn-start-case-modal';
import { CidCvnDownloadButton } from '../components/cid-cvn-download-button';
import { CidCvnReportContent } from '../components/cid-cvn-report-content';
import { CidCvnRightAside } from '../components/cid-cvn-right-aside';
import { createUseStyles } from 'react-jss';
import { DATE_FORMAT } from '@web/config/constants';
import { DetailView, DetailViewAside, DetailViewContent, DetailViewHeader } from '../../components/detail-view';
import { generateCidCvnPdf } from '../cid-cvn-pdf/cid-cvn.pdf';
import { getCidCvnPdfOptions, getCidCvnResultMessages } from '../cid-cvn-pdf/cid-cvn-pdf-helpers';
import { getDisplayValue, getDisplayDate } from '@web/utils';
import { Icon, StsIconName, Typography } from '@packages/ui/shared';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { TechlineStatusCode } from '@packages/models/api';
import { ToasterType } from '@web/components/toast-notification';
import { useCidCvn, useCidCvnState } from '@packages/contexts/cid-cvn';
import { useCreateCidCvnCaseWorksheet } from '../hooks/useCreateCidCvnCaseWorksheet';
import { useAuthState } from '@packages/contexts/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';
import ToastNotification from '@web/components/toast-notification';

const logger = getLogger('cid-cvn');

type ToastState = {
    show: boolean;
    message: string;
    iconName?: StsIconName;
    variant?: ToasterType;
};

const useStyles = createUseStyles({
    aside: {
        borderRight: `1px solid ${colors.grayThree}`,
        width: 248,
        height: '100%',
    },
});

export function CidCvnReport() {
    const classes = useStyles();
    const navigate = useNavigate();
    const { account } = useAuthState();
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const { createCaseAndWorksheetSubmission } = useCreateCidCvnCaseWorksheet();
    const [data, setData] = useState<any>(null);
    const [alertBarMessage, setAlertBarMessage] = useState<{ message: string }>({ message: '' });
    const [isCreateCaseLoading, setIsCreateCaseLoading] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showStartCaseModal, setShowStartCaseModal] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<ToastState>({ show: false, message: '' });
    const { selectedReportHeader } = useCidCvnState();
    const { setSelectedReportHeader, clearSelectedReportHeader } = useCidCvn(cidCvnService);
    const createdDate = getDisplayDate(data?.createdTimestamp, DATE_FORMAT.MON_DAY_YEAR_AT_H_MM);
    const { resultMessage, resultMessageReasons } = getCidCvnResultMessages(data?.result, createdDate, t);

    const isDownloadButtonDisabled = isDownloading || isLoading || !data;

    const isCidCvnReportHeaderLoading = !selectedReportHeader?.reportName && !selectedReportHeader?.result && isLoading;

    function handleHideModal() {
        setShowStartCaseModal(false);
        setAlertBarMessage({ message: '' });
    }

    async function fetchCidCvnReport(id: string) {
        setIsLoading(true);
        try {
            const response = await cidCvnService.getCidCvnReport(id);
            if (!response.success) {
                throw response.data;
            }
            setData(response.data);

            const { cidCvnStatusId, reportName, result } = response.data;

            if (
                cidCvnStatusId !== selectedReportHeader.id ||
                reportName !== selectedReportHeader.reportName ||
                result !== selectedReportHeader.result
            ) {
                setSelectedReportHeader({ id: cidCvnStatusId, reportName, result });
            }
        } catch (error) {
            const errorMessage = error.message || 'An unexpected error occurred.';
            logger.error(`Failed to fetch the CID/CVN report [${id}]`, { error, reportId: id });
            alert(errorMessage);
            clearSelectedReportHeader();
        } finally {
            setIsLoading(false);
        }
    }

    const handleDownload = useCallback(() => {
        setIsDownloading(true);
        setTimeout(() => {
            try {
                const pdfOptions = getCidCvnPdfOptions(data, t);
                if (pdfOptions) {
                    generateCidCvnPdf(pdfOptions);
                    setShowToast({
                        show: true,
                        message: t('cidCvnCheck:report.downloadSuccessMessage'),
                    });
                } else {
                    setShowToast({
                        show: true,
                        message: t('cidCvnCheck:report.downloadErrorMessage'),
                        iconName: 'x-close',
                        variant: 'error',
                    });
                }
            } catch (error) {
                logger.error(`Failed to download the CID/CVN report PDF [${id}]`, { error, reportId: id });
                setShowToast({
                    show: true,
                    message: t('cidCvnCheck:report.downloadErrorMessage'),
                    iconName: 'x-close',
                    variant: 'error',
                });
            } finally {
                setIsDownloading(false);
            }
        }, 1000);
    }, [data, t]);

    function navigateToCidCvnCaseWorksheet(caseNumber?: string) {
        if (!caseNumber) {
            return;
        }
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_OPENS_EXISTING_CASE);
        navigate(`/vin/${data.vin}/techline/${caseNumber}/cid-cvn-worksheet`);
    }

    function navigateToTechlineCase(caseNumber?: string) {
        if (!caseNumber) {
            return;
        }
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_OPENS_EXISTING_CASE);
        window.open(`/techline/${caseNumber}`, '_blank', 'noopener,noreferrer');
    }

    const handleNavigateToTechlineCase = useCallback(() => {
        if (data?.techlineCaseStatus === TechlineStatusCode.DRAFT) {
            navigateToCidCvnCaseWorksheet(data?.techlineCaseNumber);
            return;
        }
        navigateToTechlineCase(data?.techlineCaseNumber);
    }, [data?.techlineCaseNumber, data?.techlineCaseStatus]);

    async function handleStartCaseButtonClick() {
        analyticsService.logEvent(ANALYTICS_EVENTS.CID_CVN_TECHLINE_CASE_STARTED, {
            moduleName: 'cid_cvn',
            sourceModule: 'cid_cvn',
            targetModule: 'techline',
            entryPoint: 'cid_cvn_result',
            resultStatus: data?.result?.toLowerCase(),
        });
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_CLICKS_CREATE_A_TECHLINE_CASE);
        if (!data || !account?.soaUsername) {
            return;
        }
        setIsCreateCaseLoading(true);

        try {
            const { isValid, message } = await validateUserCanCreateCaseForRetailer();
            if (!isValid) {
                setAlertBarMessage({ message });
                return;
            }

            const { caseNumber } = await createCaseAndWorksheetSubmission({
                retailerNumber: data.retailerInfo.code,
                userName: account.soaUsername,
                vin: data.vin,
                cid: data.cid,
                cvn: data.cvn,
                cidCvnStatusId: data.cidCvnStatusId,
                cpoInd: data.cpoInd,
            });
            navigate(`/vin/${data.vin}/techline/${caseNumber}/cid-cvn-worksheet`);
        } catch (error) {
            setAlertBarMessage({
                message:
                    error instanceof Error
                        ? error.message
                        : 'We encountered an unexpected error. Please refresh and try again.',
            });
            logger.error(`Failed to create the draft case for VIN [${data.vin}]`, { error, vin: data.vin });
        } finally {
            setIsCreateCaseLoading(false);
        }
    }

    async function validateUserCanCreateCaseForRetailer(): Promise<{ isValid: boolean; message: string }> {
        try {
            const response = await techlineService.retrieveRetailer(true, undefined, data?.retailerInfo?.code);

            if (!response.success) {
                return {
                    isValid: false,
                    message:
                        response.data?.message ??
                        'An unexpected error occurred while retrieving retailers. Please refresh & try again.',
                };
            }

            const retailers = response?.data?.results ?? [];
            const isValid = retailers.some(
                (retailer: { retailerNumber: string }) => retailer.retailerNumber === data?.retailerInfo?.code
            );

            return isValid
                ? { isValid: true, message: '' }
                : {
                      isValid: false,
                      message:
                          'You are not authorized to create a case for this retailer. Contact Techshare for support: techshare_support@subaru.com.',
                  };
        } catch (error) {
            logger.error('Failed to fetch the retailers for the case-creation check', {
                error,
                retailerNumber: data?.retailerInfo?.code,
            });
            return {
                isValid: false,
                message: 'Error fetching retailers. Please refresh & try again.',
            };
        }
    }

    useEffect(() => {
        if (id) {
            fetchCidCvnReport(id);
        }

        return () => {
            clearSelectedReportHeader();
        };
    }, [id]);

    return (
        <>
            <DetailView>
                <DetailViewHeader hasBack>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex justify-content-between align-items-center">
                            {isCidCvnReportHeaderLoading ? (
                                <div className="d-flex">
                                    <SkeletonLoader type="text" height={26} width={180} className="m-0" />
                                    <SkeletonLoader type="text" height={26} width={48} className="mb-0 ml-6" />
                                </div>
                            ) : (
                                <div className="d-flex justify-content-between align-items-center">
                                    <Typography variant="h3">
                                        {getDisplayValue(selectedReportHeader.reportName ?? data?.reportName)}
                                    </Typography>

                                    <div className="ml-6">
                                        <CidCvnCheckResultBadge result={selectedReportHeader.result ?? data?.result} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <CidCvnDownloadButton
                            onPress={handleDownload}
                            isDisabled={isDownloadButtonDisabled}
                            isLoading={isDownloading}
                        />
                    </div>
                </DetailViewHeader>
                <DetailViewContent>
                    <DetailViewAside classNames={classes.aside}>
                        <Typography variant="caption6" color="textDarkSecondary">
                            CID CVN Check
                        </Typography>
                    </DetailViewAside>
                    <CidCvnReportContent
                        caseNumber={data?.techlineCaseNumber}
                        caseStatus={data?.techlineCaseStatus}
                        cid={data?.cid}
                        cvn={data?.cvn}
                        createdBy={data?.createdAccountName}
                        createdDate={data?.createdTimestamp}
                        fileName={data?.fileName}
                        imageUrl={data?.imageUrl}
                        isLoading={isLoading}
                        result={data?.result}
                        resultMessage={resultMessage}
                        resultMessageReasons={resultMessageReasons}
                        showDetails={data?.canViewDiagnosticInfo}
                        onNavigateToTechlineCase={handleNavigateToTechlineCase}
                        onShowStartTechlineCaseModal={() => setShowStartCaseModal(true)}
                    />
                    <CidCvnRightAside
                        createdBy={data?.createdAccountName}
                        createdDate={createdDate}
                        isLoading={isLoading}
                        retailer={data?.retailerInfo}
                        showDetails={data?.canViewDiagnosticInfo}
                    />
                </DetailViewContent>
            </DetailView>

            <CidCvnStartCaseModal
                isLoading={isCreateCaseLoading}
                show={showStartCaseModal}
                onHide={handleHideModal}
                onStart={handleStartCaseButtonClick}
                alertBarMessage={alertBarMessage}
                onCloseAlertBar={() => setAlertBarMessage({ message: '' })}
            />

            <ToastNotification
                show={showToast.show}
                onClose={() => setShowToast({ ...showToast, show: false })}
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name={showToast.iconName ?? 'checkmark'} size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {showToast.message}
                        </Typography>
                    </div>
                }
                autoHideTime={2000}
                variant={showToast.variant ?? 'success'}
                position="top-center"
            />
        </>
    );
}
